import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep,
} from 'lodash';
import clsx from 'clsx';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';

import InfoIcon from '@mui/icons-material/Info';

import Push from '../../../components/push';
import Validator, { useValidate } from '../../../components/validator';

import PolicyService from '../../../data/policy-service';
import Auth from '../../../data/auth-service';

import Animations from '../../../libraries/animations';

const useStyles = makeStyles(theme => ({
    formControl: {
        margin: theme.spacing(1),
        width: '100%',
    },
    buttonGroup: {
        '& button': {
            float: 'right',
            marginLeft: theme.spacing(1),
        },
    },
    ...Animations.Push(theme),
    warningBox: {
        border: `1px solid ${theme.palette.warning.main}`,
        background: theme.styled.paper.background,
        padding: theme.spacing(2),
    },
}));

const PacketCapture = () => {
    const classes = useStyles();
    const params = useParams();
    const location = useLocation();
    const pcapPerms = Auth.permissions.get('PcapPolicy');
    const [readonly, setReadonly] = useState(!pcapPerms.write);
    const [policy, setPolicy] = useState({
        count: 0,
        name: 'default',
        type: 'pcap_config',
        data: {
            smart_tdiff: 11,
            system_bpf: '',
            bpf: '',
        },
    });
    const pcapForm = useValidate({});
    const [original, setOriginal] = useState();
    const [changes, setChanges] = useState(false);
    const [mode, setMode] = useState('pcap');
    const [pushing, setPushing] = useState(false);

    const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';

    useEffect(() => {
        getPolicy();
    }, []);

    useEffect(() => {
        setChanges(!_isEqual(policy, original));
    }, [policy]);

    const getPolicy = () => {
        PolicyService.getPolicy('pcap_config', params.policy).then(res => {
            setOriginal(_cloneDeep(res));
            setPolicy(res);
            setReadonly(readonly || (pcapPerms.override ? false : (!MC2CHECK && (res.source === 'mc2' || location.pathname.includes('.mc2')))));
        }, err => {
            if (typeof err === 'string') {
                toast.error(err);
            }
        });
    };

    const handleInfo = (e) => {
        window.open('http://www.tcpdump.org/manpages/pcap-filter.7.html');
    };

    const handlePushPolicy = () => {
        setMode('push');
    };

    const handleDeployPolicy = (sensorList) => {
        setPushing(true);
        PolicyService.updatePolicy('pcap_config', params.policy, policy).then(res => {
            setMode('pcap');
            toast.success('Policy saved successfully');
            getPolicy();

            if (sensorList.length || Object.keys(sensorList).length) {
                PolicyService.assignPolicy('pcap_config', params.policy, sensorList).then(() => {
                    toast.success('Policy pushed to new sensor(s) successfully');
                }, err => {
                    if (typeof err === 'string') {
                        toast.error('Pushing policy to new sensor(s) failed : ' + err);
                    } else {
                        toast.error(err.message + ', ' + err.error);
                    }
                });
            }
        }, err => {
            if (err) {
                toast.error(err.message + ': ' + err.error);
            }
        }).finally(() => { setPushing(false) });
    };

    const handleRevertPolicy = () => {
        getPolicy();
    };

    const handleUpdatePolicy = (e, validID, valid) => {
        if (validID) {
            pcapForm.set({ ...pcapForm.get(), [validID]: valid });
        }

        const p = { ...policy };
        if (e.target.name === 'smart_tdiff') {
            p.data[e.target.name] = parseInt(e.target.value) || '';
        } else {
            p.data[e.target.name] = e.target.value;
        }
        setPolicy(p);
    };

    const handleModeChange = () => {
        setMode('pcap');
    };

    return (
        <Grid container spacing={2}>
            <Grid container item xs={12} style={{ display: mode === 'push' ? 'none' : 'flex' }}>
                <Grid item xs={12} display="flex" justifyContent="space-between">
                    <Box>
                        {!MC2CHECK && policy.source === 'mc2' && <Box mb={1} className={classes.warningBox} display="inline-block">
                            You cannot modify or push a policy assigned by an MC2.
                        </Box>}
                    </Box>
                    <Box display="flex" mb={2}>
                        <Box ml={1}><Button variant="contained" aria-label="info" onClick={handleInfo}><InfoIcon/></Button></Box>
                        {pcapPerms.admin && <Box ml={1}><Button
                            variant="contained"
                            color="primary"
                            aria-label="push"
                            onClick={handlePushPolicy}
                            disabled={!MC2CHECK && policy.source === 'mc2'}
                            className={clsx({
                                [classes.pushAnimation]: !(!MC2CHECK && policy.source === 'mc2') && changes,
                            })}
                        >Push</Button></Box>}
                        {!readonly && <Box ml={1}><Button variant="contained" color="secondary" aria-label="revert" onClick={handleRevertPolicy} disabled={!MC2CHECK && policy.source === 'mc2'}>Revert</Button></Box>}
                    </Box>
                </Grid>
            </Grid>
            <Grid item xs={12} style={{ display: mode === 'push' ? '' : 'none' }}>
                {pcapPerms.admin && <Push
                    policyName={params.policy}
                    policyType="pcap_config"
                    onDeploy={handleDeployPolicy}
                    closePush={handleModeChange}
                    pushing={pushing}
                    permName={'PcapPolicy'}
                />}
            </Grid>
            <Grid container spacing={2} style={{ display: mode === 'pcap' ? '' : 'none' }}>
                <Grid item xs={12}>
                    <Paper elevation={3}>
                        <Box p={2}>
                            <Typography variant="h5">
                                Smart PCAP Settings
                            </Typography>
                            <Box mt={1}><Validator
                                disabled={readonly}
                                valueType="range"
                                errorText="Time delta must be with 0 and 42 seconds"
                                fullWidth
                                type="number"
                                label="Time delta"
                                helperText="The amount of time before and after an alert to save packets for."
                                inputProps={{
                                    min: 0,
                                    max: 42,
                                }}
                                endAdornment={<InputAdornment position='end'>Seconds</InputAdornment>}
                                name="smart_tdiff"
                                onChange={handleUpdatePolicy}
                                value={policy.data.smart_tdiff}
                                aria-label="smart_tdiff"
                                variant="standard"
                            /></Box>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12}>
                    <Paper>
                        <Box p={2}>
                            <Typography variant="h5">
                                Analyze and capture traffic matching the following BPF filter
                            </Typography>
                            <Box mt={1}><TextField
                                disabled={readonly}
                                fullWidth
                                label="System BPF filter expression"
                                helperText="An empty filter catches everything"
                                onChange={handleUpdatePolicy}
                                name="system_bpf"
                                value={policy.data.system_bpf}
                                aria-label="system_bpf"
                                variant="standard"
                            /></Box>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12}>
                    <Paper>
                        <Box p={2}>
                            <Typography variant="h5">
                                Capture traffic matching the following BPF Filter
                            </Typography>
                            <Box mt={1}><TextField
                                disabled={readonly}
                                fullWidth
                                label="Capture BPF filter expression"
                                helperText="An empty filter catches everything"
                                onChange={handleUpdatePolicy}
                                name="bpf"
                                value={policy.data.bpf}
                                aria-label="bpf"
                                variant="standard"
                            /></Box>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default PacketCapture;
